import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Branding images
const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

// Register Chart.js modules
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function TableNo20() {
  const pdfRef = useRef();

  // Data
  const data = {
    years: ["2018", "2019", "2020", "2021", "2022", "2023", "2024"],
    femaleEmployees: [35235, 37261, 36896, 40558, 43733, 43660, 44039],
  };

  // Chart Data
  const chartData = {
    labels: data.years,
    datasets: [
      {
        label: "Total Female Employees",
        data: data.femaleEmployees,
        backgroundColor: "rgba(236, 72, 153, 0.8)",
        borderRadius: 8,
      },
    ],
  };

  // Chart Options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Number of Female Employees (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Year",
          font: { size: 14, weight: "bold" },
        },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { callback: (value) => value.toLocaleString("en-US") },
        title: {
          display: true,
          text: "Number of Female Employees",
          font: { size: 14, weight: "bold" },
        },
      },
    },
  };

  // Insights
  const start = data.femaleEmployees[0];
  const end = data.femaleEmployees[data.femaleEmployees.length - 1];
  const growthPercent = (((end - start) / start) * 100).toFixed(1);
  const peak = Math.max(...data.femaleEmployees);
  const peakYear = data.years[data.femaleEmployees.indexOf(peak)];

  // PDF Download
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2, // higher for sharper text
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      // A4 size in mm
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Convert canvas height to mm
      const imgWidth = pageWidth - 10; // leave small margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      // Header
      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      // Footer
      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // More pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Footer for the last page
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("number_of_female_employees.pdf");
      // input.style.width = originalWidth;
    });
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-pink-50 via-white to-pink-100 rounded-xl shadow-lg"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Number of Female Employees (2018–2024)
        </h2>

        {/* Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-pink-100 rounded-xl shadow">
            <p className="text-sm font-medium text-gray-600">2018 Employees</p>
            <p className="text-xl font-bold text-pink-800">
              {start.toLocaleString("en-US")}
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm font-medium text-gray-600">2024 Employees</p>
            <p className="text-xl font-bold text-green-800">
              {end.toLocaleString("en-US")}
            </p>
          </div>
          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm font-medium text-gray-600">
              Growth (2018–2024)
            </p>
            <p className="text-xl font-bold text-yellow-800">
              +{growthPercent}%
            </p>
          </div>
        </div>

        {/* Table + Chart Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col h-full justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2">Year</th>
                    {data.years.map((year, index) => (
                      <th key={index} className="p-2">
                        {year}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-pink-50 hover:bg-gray-100">
                    <td className="p-2 font-semibold text-pink-800">
                      Female Employees
                    </td>
                    {data.femaleEmployees.map((val, i) => (
                      <td key={i} className="p-2">
                        {val.toLocaleString("en-US")}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>

        {/* Peak Info */}
        <div className="text-center mt-4 text-gray-700">
          <p className="text-sm">
            📌 The highest number of female employees was recorded in{" "}
            <span className="font-semibold text-pink-700">{peakYear}</span> with{" "}
            <span className="font-semibold text-pink-700">
              {peak.toLocaleString("en-US")}
            </span>{" "}
            employees.
          </p>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
